import { useId, useMemo, type CSSProperties } from "react";
import { useAppIconLookup } from "@/hooks/useAppIconLookup";
import { AppIcon } from "@/components/applications/AppIcon";
import { WebClipFavicon } from "./WebClipFavicon";
import type { IconItem, FormFactor, IconKind } from "./types";
import { Folder } from "lucide-react";
import { cn } from "@/lib/utils";

interface IconTileProps {
  item: IconItem;
  iconUrl?: string;
  size?: "sm" | "md";
  onClick?: () => void;
  selected?: boolean;
  /**
   * When true, suppresses the visible text label under the icon. The label
   * remains available via aria-label so assistive tech still announces it.
   * Used by the dock, which mirrors real iOS/iPadOS — dock items show only
   * the icon to avoid cramped, overlapping labels.
   */
  hideLabel?: boolean;
  /**
   * Fired when a Web Clip's favicon probe produces a verdict and the item
   * doesn't yet have a persisted IconKind. Used by the editor to backfill
   * older layouts so the result survives refresh / export.
   */
  onWebClipIconKindResolved?: (url: string, kind: IconKind) => void;
}

export function IconTile({ item, iconUrl, size = "md", onClick, selected, hideLabel, onWebClipIconKindResolved }: IconTileProps) {
  const px = size === "sm" ? "h-9 w-9" : "h-12 w-12";
  const label = labelOf(item);
  // When the label is suppressed (dock tiles), the button must collapse to
  // exactly the icon's intrinsic box. Reset every property that could add
  // vertical space from a hidden text node or a stray whitespace child:
  //  - `p-0` removes any inherited button padding from UA stylesheets.
  //  - `leading-none` zeroes the inline-box line-height so an empty text
  //    run can't push the icon downward while a webfont is still loading
  //    (FOUT can briefly inflate `1em` line-height before the icon font
  //    metrics settle).
  //  - `text-[0px]` zeroes font-size so any whitespace/zero-width text
  //    nodes (e.g. JSX whitespace between siblings) contribute no height.
  //  - `align-middle` keeps the icon flex item from being baseline-aligned
  //    against a phantom text run.
  // The icon child re-establishes a normal text size for its own contents
  // via `AppIcon` / `WebClipFavicon` (each owns its own typography), so
  // this reset is scoped to the wrapper button only.
  const labelOnlyReset = "p-0 text-[0px] align-middle";
  const layoutClass = hideLabel
    ? `flex items-center justify-center ${labelOnlyReset} focus:outline-none`
    : "flex flex-col items-center gap-1 focus:outline-none";
  const layoutClassWithGroup = hideLabel
    ? `flex items-center justify-center ${labelOnlyReset} group focus:outline-none`
    : "flex flex-col items-center gap-1 group focus:outline-none";
  // line-height is enforced via inline style so tailwind-merge can't drop it
  // while collapsing adjacent typography utilities.
  const layoutStyle: CSSProperties | undefined = hideLabel
    ? { lineHeight: 0 }
    : undefined;
  if (item.Type === "Folder") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          layoutClassWithGroup,
          selected && "ring-2 ring-primary rounded-lg p-1"
        )}
        aria-label={`Folder ${label}`}
        style={layoutStyle}
      >
        <div
          className={cn(
            px,
            "rounded-xl bg-muted/60 backdrop-blur grid grid-cols-3 grid-rows-3 gap-px p-1 group-hover:bg-muted"
          )}
        >
          {Array.from({ length: 9 }).map((_, i) => {
            const nested = item.Pages?.[0]?.[i];
            return (
              <div
                key={i}
                className="bg-background/80 rounded-sm flex items-center justify-center text-[6px] text-muted-foreground"
              >
                {nested ? "•" : ""}
              </div>
            );
          })}
        </div>
        {!hideLabel && (
          <span className="text-[10px] text-foreground/80 truncate max-w-[64px]">{label}</span>
        )}
      </button>
    );
  }
  // Web Clips get a dedicated favicon component that distinguishes "no real
  // favicon" (generic globe glyph) from a successfully resolved site icon.
  if (item.Type === "WebClip") {
    const persistedKind = item.IconKind;
    // Stable, render-scoped id so the button can point to a hidden
    // description node that explains the persisted IconKind verdict.
    // We use aria-describedby (in addition to keeping the verdict in
    // aria-label) so assistive tech can surface the source of the icon
    // as supplementary detail rather than collapsing it into the name.
    const describedById = useId();
    const verdictDescription = persistedKind
      ? persistedKind === "favicon"
        ? "Icon source: real site favicon."
        : "Icon source: generic fallback (no site favicon found)."
      : null;
    const ariaLabel = persistedKind
      ? `${label} (icon: ${persistedKind === "favicon" ? "favicon" : "generic fallback"})`
      : label;
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          layoutClass,
          selected && "ring-2 ring-primary rounded-lg p-1"
        )}
        aria-label={ariaLabel}
        aria-describedby={verdictDescription ? describedById : undefined}
        style={layoutStyle}
      >
        <WebClipFavicon
          url={item.URL}
          iconSrc={iconUrl ?? item.Icon ?? null}
          iconKind={persistedKind ?? null}
          label={label}
          className={px}
          onIconKindResolved={
            !persistedKind && onWebClipIconKindResolved
              ? (kind) => onWebClipIconKindResolved(item.URL, kind)
              : undefined
          }
        />
        {!hideLabel && (
          <span className="text-[10px] text-foreground/80 truncate max-w-[64px]">{label}</span>
        )}
        {persistedKind && (
          <span
            aria-hidden="true"
            data-testid={`webclip-iconkind-badge-${persistedKind}`}
            className={cn(
              "text-[8px] uppercase tracking-wide font-medium leading-none",
              "rounded px-1 py-px",
              persistedKind === "favicon"
                ? "bg-success-muted text-success-muted-foreground"
                : "bg-muted text-muted-foreground"
            )}
          >
            {persistedKind === "favicon" ? "Favicon" : "Generic"}
          </span>
        )}
        {verdictDescription && (
          <span
            id={describedById}
            data-testid={`webclip-iconkind-description-${persistedKind}`}
            className="sr-only"
          >
            {verdictDescription}
          </span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        layoutClass,
        selected && "ring-2 ring-primary rounded-lg p-1"
      )}
      aria-label={label}
      style={layoutStyle}
    >
      <AppIcon iconSrc={iconUrl ?? null} appName={label} className={cn(px, "rounded-xl")} />
      {!hideLabel && (
        <span className="text-[10px] text-foreground/80 truncate max-w-[64px]">{label}</span>
      )}
    </button>
  );
}

export function labelOf(item: IconItem): string {
  if (item.Type === "Folder") return item.DisplayName || "Folder";
  if (item.Type === "WebClip") return item.DisplayName || item.URL;
  return item.DisplayName || item.BundleID;
}

export function bundleIdsOf(items: IconItem[]): string[] {
  const ids: string[] = [];
  for (const it of items) {
    if (it.Type === "Application" && it.BundleID) ids.push(it.BundleID);
    if (it.Type === "Folder") ids.push(...bundleIdsOf(it.Pages.flat()));
  }
  return ids;
}

export function useIconUrlMap(items: IconItem[]): Record<string, string> {
  const apps = useMemo(
    () => bundleIdsOf(items).map((bundleId) => ({ bundleId })),
    [items]
  );
  return useAppIconLookup(apps);
}

export function FolderBadge({ ff }: { ff: FormFactor }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
      <Folder className="h-3 w-3" /> {ff}
    </span>
  );
}
